import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { QrCode, Bell, ShoppingCart, User, Package, BarChart3, Heart, Users } from 'lucide-react';
import SectionEyebrow from '@/components/SectionEyebrow';
import { FadeIn, Stagger, StaggerItem } from '@/components/motion-primitives';

const Modules = () => {
  const modules = [
    {
      icon: QrCode,
      title: 'QR Menü Yönetimi',
      description:
        'Dijital menülerinizi kolayca oluşturun ve güncelleyin. Müşterileriniz telefon ile menüye erişebilsin.',
      features: ['Sınırsız menü oluşturma', 'Anında güncelleme', 'Görsel zengin menü'],
    },
    {
      icon: Bell,
      title: 'Bildirim Sistemi',
      description: 'Siparişlerden satışlara kadar tüm önemli olaylardan anında haberdar olun.',
      features: ['Anlık bildirimler', 'Özelleştirilebilir uyarılar', 'Mobil ve web desteği', 'Sesli bildirimler'],
    },
    {
      icon: ShoppingCart,
      title: 'Sipariş Yönetimi',
      description: 'Gelen siparişleri etkin şekilde yönetin, takip edin ve müşteri memnuniyetini artırın.',
      features: ['Gerçek zamanlı sipariş takibi', 'Otomatik durum güncellemesi', 'Sipariş geçmişi', 'Toplu işlemler'],
    },
    {
      icon: User,
      title: 'Profil Yönetimi',
      description: 'Müşteri profillerini yönetin, tercihlerini kaydedin ve kişiselleştirilmiş hizmet sunun.',
      features: ['Müşteri veri tabanı', 'Sipariş geçmişi', 'Tercih yönetimi', 'İletişim bilgileri'],
    },
    {
      icon: Package,
      title: 'Ürün Yönetimi',
      description: 'Stok kontrolü, fiyat yönetimi ve ürün katalogunu tek yerden kontrol edin.',
      features: ['Fiyat yönetimi', 'Kategori oluşturma', 'Toplu ürün işlemleri'],
    },
    {
      icon: BarChart3,
      title: 'Raporlama Altyapısı',
      description: 'Detaylı satış raporları ve analizlerle işinizin performansını izleyin.',
      features: ['Günlük/aylık raporlar', 'Satış analizleri', 'Excel çıktısı'],
    },
    {
      icon: Heart,
      title: 'Sadakat Sistemi',
      description: 'Müşteri sadakatini artıracak kampanyalar oluşturun ve yönetin.',
      features: ['Puan sistemi', 'Kampanya yönetimi', 'Müşteri segmentasyonu', 'Otomatik ödüller'],
    },
    {
      icon: Users,
      title: 'Personel Yönetimi',
      description: 'Çalışanlarınızı yönetin, görev dağılımı yapın ve performanslarını takip edin.',
      features: ['Kullanıcı rolleri', 'Yetki yönetimi', 'Performans takibi'],
    },
  ];

  return (
    <section id="modules" className="relative section-y-lg bg-surface-2 overflow-hidden">
      <div className="absolute inset-0 bg-grid-pattern opacity-60" aria-hidden="true" />

      <div className="container relative mx-auto px-4">
        <FadeIn className="text-center mb-16">
          <SectionEyebrow className="mb-4">Güçlü Modüller</SectionEyebrow>
          <h2 className="type-h2 text-balance mb-4">
            İşinizi Yönetmenin <span className="text-gradient">En Kolay Yolu</span>
          </h2>
          <p className="type-body-lg text-pretty max-w-2xl mx-auto">
            Her işletmenin ihtiyacına özel tasarlanmış modüllerle işinizi daha verimli hale getirin.
          </p>
        </FadeIn>

        <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" stagger={0.06}>
          {modules.map((module, index) => (
            <StaggerItem key={index}>
              <Card className="group h-full hover:shadow-card-lift transition-all duration-300 border border-hairline bg-surface">
                <CardHeader className="text-center pb-4">
                  <div className="mx-auto w-12 h-12 bg-brand-50 text-brand-700 rounded-xl flex items-center justify-center group-hover:bg-brand-600 group-hover:text-white transition-colors duration-300">
                    <module.icon className="w-6 h-6" aria-hidden="true" />
                  </div>
                  <CardTitle className="text-lg font-semibold text-ink group-hover:text-brand-700 transition-colors font-poppins">
                    {module.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <CardDescription className="text-ink-muted mb-4 text-center font-inter">
                    {module.description}
                  </CardDescription>
                  <ul className="space-y-2">
                    {module.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-sm text-ink-muted font-inter">
                        <div className="w-1.5 h-1.5 bg-brand-500 rounded-full mr-2 flex-shrink-0" aria-hidden="true" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </StaggerItem>
          ))}
        </Stagger>

        {/* Integration Section */}
        <FadeIn delay={0.1} className="mt-20 text-center">
          <div className="relative overflow-hidden rounded-3xl border border-hairline bg-surface p-10 lg:p-14">
            <h3 className="text-2xl lg:text-3xl font-bold text-ink mb-4 font-poppins tracking-tight">
              Tüm Modüller Tek Platformda
            </h3>
            <p className="text-ink-muted max-w-2xl mx-auto font-inter">
              Birbirinden farklı sistemlerle uğraşmak yerine, tüm ihtiyaçlarınızı tek bir platformdan yönetin. Modüller
              arası entegrasyon sayesinde verileriniz otomatik olarak senkronize olur.
            </p>
          </div>
        </FadeIn>
      </div>
    </section>
  );
};

export default Modules;
